/*
 * Marquoir : moteur de mouvement de la boutique (contrat §5).
 *
 * Le mouvement est une amélioration. Sans JavaScript, sans GSAP ou avec « réduire les animations »,
 * rien ne bouge et tout reste lisible : aucune classe n'est posée.
 *
 * Quand le mouvement est permis :
 * - html.mq-motion est posée (états initiaux définis dans base.css) ;
 * - Lenis adoucit la molette, seulement avec un pointeur fin (souris, pavé tactile), jamais dans l'éditeur ;
 *   réglage sobre (lerp 0,15, wheelMultiplier 1, pas d'inertie au doigt : CORRECTIONS-V2 §1) : l'image suit la
 *   molette de près (≈ 6 images de retard) et s'arrête vite, sans dépasser le point voulu ;
 * - UNE seule source de lissage : avec Lenis, les animations liées au défilement suivent le défilement
 *   tel quel (scrub: true) ; sans Lenis (tactile), un léger rattrapage (scrub: 0,35). Valeur exposée : api.scrub ;
 * - ScrollTrigger suit Lenis ; CustomEase « pose » = cubic-bezier(.2, 0, 0, 1) ;
 * - seules transform, opacity, couleur et tracé SVG sont animés : jamais une propriété de mise en page ;
 * - les attributs data-mq-anim sont animés :
 *     lignes      révélation ligne par ligne (SplitText, masque), une fois ;
 *     apparition  opacité + 16 px, une fois (data-mq-cascade : enfants l'un après l'autre, valeur = pas en s) ;
 *     trace       tracé des path, line… SVG internes (data-mq-duree : durée maximale en ms) ;
 *     surligne    mots de graphite à encre, liés au défilement ;
 *     reglet      translation horizontale liée au défilement (data-mq-sens="droite" pour l'inverse).
 *   Ce qui est déjà à l'écran au chargement n'est pas masqué puis révélé : il reste en place.
 *
 * Extension : window.Marquoir.motion.enregistrer(fn) appelle fn({ gsap, ScrollTrigger, lenis, SplitText, ease, scrub })
 * après l'initialisation, seulement si le mouvement est permis. Un script exécuté avant ce fichier
 * dépose ses fonctions dans une file, reprise ici :
 *   const M = (window.Marquoir = window.Marquoir || {});
 *   M.motion = M.motion || { file: [], enregistrer(fn) { this.file.push(fn); } };
 *   M.motion.enregistrer(({ gsap, ScrollTrigger, lenis }) => { … });
 * Ce que fn crée avec GSAP est annulé si « réduire les animations » est activé en cours de visite.
 * Après une inscription tardive, les ScrollTrigger sont triés puis recalculés dans l'ordre de la page.
 */

const M = (window.Marquoir = window.Marquoir || {});
const fileInitiale = M.motion && Array.isArray(M.motion.file) ? M.motion.file.splice(0) : [];

const SELECTEUR_FORMES = 'path, line, polyline, polygon, rect, circle, ellipse';
// Lissage unique (REFONTE §2) : Lenis seul au pointeur fin ; rattrapage court au doigt
const SCRUB_TACTILE = 0.35;
const REGLAGE_LENIS = {
  lerp: 0.15,
  wheelMultiplier: 1,
  smoothWheel: true,
  syncTouch: false,
  autoRaf: false,
  allowNestedScroll: true,
  stopInertiaOnNavigate: true,
};
// Règles de lenis/dist/lenis.css (1.3.26), plus la neutralisation de scroll-behavior des versions précédentes
const STYLE_LENIS = [
  'html.lenis,html.lenis body{height:auto}',
  '.lenis:not(.lenis-autoToggle).lenis-stopped{overflow:clip}',
  '.lenis [data-lenis-prevent],.lenis [data-lenis-prevent-wheel],.lenis [data-lenis-prevent-touch],.lenis [data-lenis-prevent-vertical],.lenis [data-lenis-prevent-horizontal]{overscroll-behavior:contain}',
  '.lenis.lenis-smooth iframe{pointer-events:none}',
  '.lenis.lenis-autoToggle{transition-property:overflow;transition-duration:1ms;transition-behavior:allow-discrete}',
  'html.lenis.lenis-smooth{scroll-behavior:auto!important}',
].join('');

const enregistres = [];
let api = null;
let contexte = null;
let enInit = false;
let lenis = null;
let prets = new WeakSet();
let ease = 'power3.out';
let scrub = SCRUB_TACTILE;
let rafraichissement = 0;

const motion = {
  permis: false,
  get ease() { return ease; },
  get lenis() { return lenis; },
  get scrub() { return scrub; },
  enregistrer(fn) {
    if (typeof fn !== 'function') return;
    enregistres.push(fn);
    if (api) {
      lancer(fn);
      rafraichirTrie();
    }
  },
  // Anime les data-mq-anim d'un fragment ajouté après le chargement
  animer(racine) {
    if (api) dansContexte(() => preparer(racine));
  },
  rafraichir() {
    rafraichirTrie();
  },
};

// Recalcul dans l'ordre de la page (haut → bas), regroupé sur une image : les scènes épinglées d'abord
function rafraichirTrie() {
  if (!api || rafraichissement) return;
  rafraichissement = requestAnimationFrame(() => {
    rafraichissement = 0;
    if (!api) return;
    const { ScrollTrigger } = api;
    if (ScrollTrigger.sort) ScrollTrigger.sort();
    ScrollTrigger.refresh();
  });
}
M.motion = motion;
fileInitiale.forEach((fn) => motion.enregistrer(fn));

function dansContexte(fn) {
  if (contexte && !enInit) contexte.add(fn);
  else fn();
}

function lancer(fn) {
  dansContexte(() => {
    try {
      fn(api);
    } catch (erreur) {
      console.error('[Marquoir] motion :', erreur);
    }
  });
}

function visibleAuDepart(el) {
  const r = el.getBoundingClientRect();
  return (r.width > 0 || r.height > 0) && r.top < window.innerHeight && r.bottom > 0;
}

// Valeurs explicites : l'état masqué de base.css (sous html.mq-motion) ne doit jamais rester
function montrer(cibles) {
  window.gsap.set(cibles, { opacity: 1, y: 0 });
}

function lignes(el, visible) {
  const { gsap, SplitText } = window;
  if (visible) return;
  if (!SplitText) {
    gsap.fromTo(el, { opacity: 0, y: 16 }, {
      opacity: 1, y: 0, duration: 0.6, ease,
      scrollTrigger: { trigger: el, start: 'top 88%', once: true },
    });
    return;
  }
  SplitText.create(el, {
    type: 'lines',
    mask: 'lines',
    autoSplit: true,
    linesClass: 'mq-split-ligne',
    aria: el.querySelector('a, button, input, select, textarea') ? 'none' : 'auto',
    onSplit(decoupe) {
      return gsap.from(decoupe.lines, {
        yPercent: 130, duration: 0.8, ease, stagger: 0.08,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      });
    },
  });
}

function apparition(el, visible) {
  const { gsap } = window;
  const cascade = el.hasAttribute('data-mq-cascade');
  const pas = cascade ? (parseFloat(el.getAttribute('data-mq-cascade')) || 0.04) : 0;
  const cibles = cascade ? Array.from(el.children) : [el];
  if (cascade) montrer(el);
  if (visible || !cibles.length) {
    montrer(cibles.length ? cibles : el);
    return;
  }
  gsap.fromTo(cibles, { opacity: 0, y: 16 }, {
    opacity: 1, y: 0, duration: 0.6, ease, stagger: pas,
    scrollTrigger: { trigger: el, start: 'top 88%', once: true },
  });
}

// Trait « non-scaling-stroke » : ses tirets se mesurent à l'écran, pas dans le dessin. Pendant le tracé,
// l'épaisseur passe en unités du dessin (même rendu), pour que la longueur mesurée soit la bonne.
function epaisseurDessin(forme) {
  const nonEchelonne = forme.getAttribute('vector-effect') === 'non-scaling-stroke'
    || getComputedStyle(forme).vectorEffect === 'non-scaling-stroke';
  if (!nonEchelonne || typeof forme.getScreenCTM !== 'function') return null;
  const m = forme.getScreenCTM();
  const echelle = m ? Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) : 0;
  if (!(echelle > 0)) return null;
  const epaisseur = parseFloat(getComputedStyle(forme).strokeWidth) || 1;
  return { attribut: forme.getAttribute('vector-effect'), style: forme.style.strokeWidth, valeur: `${epaisseur / echelle}px` };
}

function trace(el, visible) {
  if (visible) return;
  const { gsap } = window;
  const formes = el.matches(SELECTEUR_FORMES) ? [el] : Array.from(el.querySelectorAll(SELECTEUR_FORMES));
  const mesures = [];
  const restaurations = [];
  formes.forEach((forme) => {
    let longueur = parseFloat(forme.getAttribute('pathLength'));
    if (!(longueur > 0)) {
      try {
        longueur = forme.getTotalLength();
      } catch (erreur) {
        longueur = 0;
      }
      const reglage = longueur > 0 ? epaisseurDessin(forme) : null;
      if (reglage) {
        restaurations.push([forme, reglage]);
        forme.removeAttribute('vector-effect');
        forme.style.strokeWidth = reglage.valeur;
      }
    }
    if (longueur > 0) mesures.push([forme, longueur]);
  });
  if (!mesures.length) return;
  const restaurer = () => {
    restaurations.forEach(([forme, reglage]) => {
      forme.style.strokeWidth = reglage.style;
      if (reglage.attribut) forme.setAttribute('vector-effect', reglage.attribut);
    });
    restaurations.length = 0;
  };
  const dureeMax = (parseFloat(el.getAttribute('data-mq-duree')) || 800) / 1000;
  const tl = gsap.timeline({
    scrollTrigger: { trigger: el, start: 'top 85%', once: true },
    // au départ, l'épaisseur suit la taille d'affichage du moment
    onStart: () => restaurations.forEach(([forme, reglage]) => {
      const juste = epaisseurDessinApres(forme, reglage);
      if (juste) forme.style.strokeWidth = juste;
    }),
    // une fois tracé, le trait redevient plein (largeur d'écran libre de changer)
    onComplete: () => {
      gsap.set(mesures.map(([forme]) => forme), { clearProps: 'strokeDasharray,strokeDashoffset' });
      restaurer();
    },
  });
  mesures.forEach(([forme, longueur], n) => {
    gsap.set(forme, { strokeDasharray: `${longueur} ${longueur}`, strokeDashoffset: longueur });
    tl.to(forme, {
      strokeDashoffset: 0,
      duration: Math.min(dureeMax, Math.max(0.24, longueur / 1500)),
      ease,
    }, n === 0 ? 0 : '>-0.12');
  });
}

function surligne(el) {
  const { gsap, SplitText } = window;
  if (!SplitText) return;
  const styles = getComputedStyle(document.documentElement);
  const depart = styles.getPropertyValue('--graphite').trim() || '#575c66';
  const arrivee = styles.getPropertyValue('--encre').trim() || '#1a1f2b';
  SplitText.create(el, {
    type: 'words',
    wordsClass: 'mq-split-mot',
    aria: el.querySelector('a, button') ? 'none' : 'auto',
    onSplit(decoupe) {
      return gsap.fromTo(decoupe.words, { color: depart }, {
        color: arrivee, ease: 'none', duration: 0.3, stagger: 0.1,
        scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 45%', scrub },
      });
    },
  });
}

function reglet(el) {
  const { gsap } = window;
  const cadre = el.parentElement || el;
  const versDroite = el.getAttribute('data-mq-sens') === 'droite';
  const distance = () => Math.max(0, el.getBoundingClientRect().width - cadre.clientWidth);
  gsap.fromTo(el, { x: () => (versDroite ? -distance() : 0) }, {
    x: () => (versDroite ? 0 : -distance()),
    ease: 'none',
    scrollTrigger: { trigger: cadre, start: 'top bottom', end: 'bottom top', scrub, invalidateOnRefresh: true },
  });
}

// Épaisseur en unités du dessin recalculée au départ du tracé (la fenêtre a pu changer de taille)
function epaisseurDessinApres(forme, reglage) {
  if (typeof forme.getScreenCTM !== 'function') return null;
  const m = forme.getScreenCTM();
  const echelle = m ? Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) : 0;
  if (!(echelle > 0)) return null;
  forme.style.strokeWidth = reglage.style;
  const epaisseur = parseFloat(getComputedStyle(forme).strokeWidth) || 1;
  return `${epaisseur / echelle}px`;
}

const ANIMATIONS = { lignes, apparition, trace, surligne, reglet };

function preparer(racine = document) {
  if (!racine || typeof racine.querySelectorAll !== 'function') return;
  const elements = Array.from(racine.querySelectorAll('[data-mq-anim]'));
  if (racine !== document && racine.matches && racine.matches('[data-mq-anim]')) elements.unshift(racine);
  // toutes les mesures d'abord, les écritures ensuite
  const visibles = elements.map((el) => visibleAuDepart(el));
  elements.forEach((el, n) => {
    if (prets.has(el)) return;
    prets.add(el);
    const animer = ANIMATIONS[el.getAttribute('data-mq-anim')];
    if (!animer) return;
    try {
      animer(el, visibles[n]);
    } catch (erreur) {
      console.error('[Marquoir] motion :', erreur);
      montrer(el);
    }
  });
}

function tickLenis(temps) {
  if (lenis) lenis.raf(temps * 1000);
}

function creerLenis(gsap, ScrollTrigger) {
  const Lenis = window.Lenis;
  if (typeof Lenis !== 'function') return null;
  if (!window.matchMedia('(pointer: fine)').matches) return null;
  if (window.Shopify && window.Shopify.designMode) return null;
  if (!document.getElementById('mq-lenis-style')) {
    const style = document.createElement('style');
    style.id = 'mq-lenis-style';
    style.textContent = STYLE_LENIS;
    document.head.appendChild(style);
  }
  const instance = new Lenis(REGLAGE_LENIS);
  instance.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(tickLenis);
  gsap.ticker.lagSmoothing(0);
  return instance;
}

function demarrer() {
  const { gsap, ScrollTrigger, SplitText, CustomEase } = window;
  gsap.registerPlugin(...[ScrollTrigger, SplitText, CustomEase].filter(Boolean));
  if (CustomEase) {
    CustomEase.create('pose', '0.2,0,0,1');
    ease = 'pose';
  }
  ScrollTrigger.config({ ignoreMobileResize: true });

  gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', (ctx) => {
    contexte = ctx;
    enInit = true;
    prets = new WeakSet();
    document.documentElement.classList.add('mq-motion');
    motion.permis = true;
    lenis = creerLenis(gsap, ScrollTrigger);
    scrub = lenis ? true : SCRUB_TACTILE;
    api = { gsap, ScrollTrigger, lenis, SplitText: SplitText || null, ease, scrub };
    try {
      // sections d'abord : leurs scènes épinglées changent la hauteur de la page
      enregistres.forEach(lancer);
      preparer(document);
    } finally {
      enInit = false;
    }
    // recalcul dans l'ordre de la page (haut → bas), quel que soit l'ordre de création (règle GSAP)
    if (ScrollTrigger.sort) ScrollTrigger.sort();
    ScrollTrigger.refresh();
    return () => {
      api = null;
      contexte = null;
      motion.permis = false;
      scrub = SCRUB_TACTILE;
      if (rafraichissement) cancelAnimationFrame(rafraichissement);
      rafraichissement = 0;
      if (lenis) {
        gsap.ticker.remove(tickLenis);
        lenis.destroy();
        lenis = null;
        gsap.ticker.lagSmoothing(500, 33);
      }
      document.documentElement.classList.remove('mq-motion');
    };
  });

  // polices chargées : les hauteurs de texte ont pu changer
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(rafraichirTrie);
  // Éditeur de thème : section rechargée ou retirée
  document.addEventListener('shopify:section:load', (evt) => {
    if (!api) return;
    motion.animer(evt.target);
    rafraichirTrie();
  });
  document.addEventListener('shopify:section:unload', (evt) => {
    if (!api) return;
    ScrollTrigger.getAll().forEach((st) => {
      if (st.trigger && evt.target.contains(st.trigger)) st.kill(true);
    });
  });
}

if (window.gsap && window.ScrollTrigger && typeof window.gsap.matchMedia === 'function') {
  try {
    demarrer();
  } catch (erreur) {
    console.error('[Marquoir] mouvement désactivé :', erreur);
    document.documentElement.classList.remove('mq-motion');
    motion.permis = false;
    api = null;
  }
}
